import styled from "@emotion/styled";
import type { FC, CSSProperties } from "react";
import { useRef, useState, useLayoutEffect } from "react";

interface CenterEllipsisTextProps {
  text: string;
  ellipsisPosition?: string;
  ellipsisWidth?: string;
  fadeMaskWidth?: string;
  className?: string;
  style?: CSSProperties;
}

const Container = styled.div`
  label: CenterEllipsisText;
  position: relative;
  --fade-mask-width: 1.4em;
  --half-ellipsis-width: calc(var(--ellipsis-width, 1.4em) / 2);

  & > .plain-text {
    white-space: nowrap;
    overflow: hidden;
    display: block;
  }

  &[data-truncated] > .plain-text {
    display: none;
  }

  &[data-truncated]:before {
    content: attr(aria-label);
    white-space: nowrap;
    overflow: hidden;
    display: block;
    width: calc(var(--ellipsis-pos, 50%) - var(--half-ellipsis-width));
    mask: linear-gradient(to left, transparent, black var(--fade-mask-width));
  }

  &[data-truncated]:after {
    content: attr(aria-label);
    white-space: nowrap;
    overflow: hidden;
    position: absolute;
    right: 0;
    top: 0;
    width: calc(100% - var(--ellipsis-pos, 50%) - var(--half-ellipsis-width));
    display: flex;
    justify-content: flex-end;
    mask: linear-gradient(to right, transparent, black var(--fade-mask-width));
  }

  & > .ellipsis-text {
    display: none;
  }

  &[data-truncated] > .ellipsis-text {
    display: block;
    position: absolute;
    left: calc(var(--ellipsis-pos, 50%));
    transform: translateX(-50%);
    top: 0;
    text-align: center;
    opacity: 0.5;
  }
`;

const Measure = styled.span`
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
`;

const CenterEllipsisText: FC<CenterEllipsisTextProps> = ({
  text,
  ellipsisPosition = "60%",
  ellipsisWidth = "0.6em",
  fadeMaskWidth,
  className,
  style
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const check = () => {
      setIsTruncated(measure.offsetWidth > container.clientWidth);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(container);
    return () => observer.disconnect();
  }, [text]);

  const cssVars: Record<string, string> = {
    "--ellipsis-pos": ellipsisPosition,
    "--ellipsis-width": ellipsisWidth
  };
  if (fadeMaskWidth) {
    cssVars["--fade-mask-width"] = fadeMaskWidth;
  }

  return (
    <Container
      ref={containerRef}
      aria-label={text}
      data-truncated={isTruncated || undefined}
      className={className}
      style={{ ...cssVars, ...style } as CSSProperties}
    >
      <Measure ref={measureRef} aria-hidden="true">
        {text}
      </Measure>
      <span className="plain-text">{text}</span>
      <span className="ellipsis-text" aria-hidden="true">
        ...
      </span>
    </Container>
  );
};

export default CenterEllipsisText;
